import { rewriteUserContacts, RewriteUserContactsErrorResponse } from '@uplift/api';
import { EMPTY_STRING } from '@uplift/constants';
import { getFormErrorsFromRewriteUserContactsErrorResponse, getRewriteUserContactsInputFromFormValues } from '@uplift/utils';
import { VALIDATION_HOME_PHONE, VALIDATION_MOBILE_PHONE } from '@uplift/validations';
import { FormikHelpers } from 'formik';
import { AxiosError } from 'axios';

import * as Yup from 'yup';
import { useUpdateSettings } from '../../hooks/use-update-settings.hook';
import { useCurrentuser } from '@uplift/contexts';
import { useUserContactsQuery } from '@uplift/queries';
import { useQueryClient } from '@tanstack/react-query';
import { ContactName } from '@uplift/types';

export type PhoneFormType = {
  [ContactName.mobile]: string;
  [ContactName.home]: string;
};
export const usePhoneForm = () => {
  const queryClient = useQueryClient();
  const me = useCurrentuser();
  const { data: contacts } = useUserContactsQuery(me.id, { suspense: true });
  const { onFormSubmitSuccess, onFormSubmitError } = useUpdateSettings();

  const phoneFormik = () => {
    const initialValues: PhoneFormType = {
      [ContactName.mobile]: contacts.find(c => c.name === ContactName.mobile)?.value ?? EMPTY_STRING,
      [ContactName.home]: contacts.find(c => c.name === ContactName.home)?.value ?? EMPTY_STRING
    };

    const validationSchema = Yup.object({
      [ContactName.mobile]: VALIDATION_MOBILE_PHONE,
      [ContactName.home]: VALIDATION_HOME_PHONE
    });

    const onSubmit = async (values: PhoneFormType, formikHelpers: FormikHelpers<PhoneFormType>) => {
      try {
        await rewriteUserContacts(
          me.id,
          getRewriteUserContactsInputFromFormValues(values)
        );

        await queryClient.invalidateQueries({ queryKey: [ 'user', me.id, 'contacts' ] });

        onFormSubmitSuccess();
      } catch (error) {
        const errorObj = getFormErrorsFromRewriteUserContactsErrorResponse(error as AxiosError<RewriteUserContactsErrorResponse>);

        if (errorObj) {
          formikHelpers.setErrors(errorObj);
          return;
        }

        onFormSubmitError();
      }
    };

    return { initialValues, validationSchema, onSubmit };
  };

  return { phoneFormik };
};
